/**
 * geolocation.js — the shared geolocation primitive (window.__geo).
 *
 * WHY THIS EXISTS
 * ---------------
 * Before this file, 18 call sites each re-derived feature detection, error
 * mapping, retry and telemetry by hand, and each got a different subset right:
 *
 *   - 8 tools (light-pollution-map, satellite-tracker, sun-moon-tracker,
 *     wind-map, meteor-showers, earthquake-map, aurora-forecast, space-weather)
 *     did `if (!navigator.geolocation) return;` — a literally silent dead click.
 *   - /share-location did the same check inside init() and RETURNED before
 *     binding any handlers, so its "Try Again" button was never wired at all.
 *     That shipped as bug report 2026-08-12 (Firefox/Android): a silent_click
 *     on #slRetryBtn, reproduced 1:1 in tests/playwright/geolocation-guard.mjs.
 *   - only /gps-speedometer got it right — secure-context check, per-platform
 *     recovery steps, telemetry on every branch. Its wisdom is generalised here.
 *
 * MEASURED FACTS THIS ENCODES (2026-08-13, Playwright, Firefox 1511 + Chromium)
 * ---------------------------------------------------------------------------
 * 1. `navigator.geolocation` can be UNDEFINED in a fully modern browser. Gecko
 *    gates it behind the `geo.enabled` pref, which hardened Android Firefox
 *    forks (Mull, IronFox) and the arkenfox user.js ship as false. Measured:
 *    firefoxUserPrefs {'geo.enabled': false} => 'geolocation' in navigator ===
 *    false. So "not supported — please try a modern browser" is a FALSE and
 *    unactionable message; the honest one names the setting.
 * 2. After a denial, a repeat getCurrentPosition() does NOT re-prompt — it
 *    re-fails instantly with code 1: measured 32/24/19 ms (Firefox) and
 *    13/0/3 ms (Chromium) across three consecutive calls, permission state
 *    'denied'. A "Try Again" button on a denial is therefore decoration: it
 *    can only redraw the same error. canRetry is false for DENIED/NO_API/
 *    INSECURE and true only for UNAVAILABLE/TIMEOUT, where a retry can win.
 * 3. Because retry cannot help a denial, recovery has to come from the user
 *    changing a browser/OS setting. onRecover() watches for exactly that
 *    (Permissions API change event + a visibilitychange re-probe for engines
 *    that do not fire it) so the tool heals itself when they come back.
 *
 * CONTRACT
 * --------
 *   __geo.available()            -> {ok, reason}          synchronous pre-flight
 *   __geo.permission()           -> Promise<state>        'granted'|'denied'|'prompt'|'unknown'
 *   __geo.get(opts)              -> Promise<position>     rejects with a normalised error
 *   __geo.watch(onPos, onErr, o) -> id | null
 *   __geo.clear(id)
 *   __geo.explain(errOrReason)   -> {reason, code, title, message, steps[], canRetry}
 *   __geo.panel(host, exp, opts) -> renders the default failure panel
 *   __geo.onRecover(cb)          -> calls cb() when permission flips to granted
 *
 * Strings come from window.__GEO_I18N (emitted by partials.geolocation from
 * tools.common.geo_*), so all 9 locales are covered in one place. A tool that
 * wants its own wording can keep its own markup and use explain() alone.
 */
(function () {
    'use strict';

    var REASON = {
        OK: 'ok',
        NO_API: 'no_api',            // navigator.geolocation absent (geo.enabled=false)
        INSECURE: 'insecure',        // not a secure context
        DENIED: 'denied',            // PositionError 1
        UNAVAILABLE: 'unavailable',  // PositionError 2
        TIMEOUT: 'timeout',          // PositionError 3
        UNKNOWN: 'unknown'
    };

    var I18N = window.__GEO_I18N || {};
    function t(key, fallback) {
        var v = I18N[key];
        return (typeof v === 'string' && v) ? v : fallback;
    }

    function slug() {
        var m = document.querySelector('meta[name="tool-slug"]');
        return m ? m.content : null;
    }

    // Every failure reports from ONE place. Before this, /share-location emitted
    // nothing at all on a geolocation failure, so its 5%-completion rate was
    // invisible in analytics and only a hand-written bug report surfaced it.
    function report(reason, code) {
        try {
            if (typeof window.__ae !== 'function') return;
            window.__ae('tool_error', {
                slug: slug(),
                stage: 'geolocation',
                reason: reason,
                code: code == null ? null : code
            });
        } catch (e) {}
    }

    function platform() {
        var ua = navigator.userAgent || '';
        if (/iPhone|iPad|iPod/i.test(ua)) return 'ios';
        if (/Android/i.test(ua)) return 'android';
        return 'desktop';
    }

    function isGecko() {
        return /Firefox|FxiOS|Gecko\//i.test(navigator.userAgent || '') &&
               !/Chrome|Chromium|Edg/i.test(navigator.userAgent || '');
    }

    /* ── Pre-flight ─────────────────────────────────────────────────────── */

    function available() {
        if (!navigator.geolocation) return { ok: false, reason: REASON.NO_API };
        // An insecure page still exposes the API but every call fails with
        // code 1, which would otherwise be misreported as a user denial.
        if (window.isSecureContext === false) return { ok: false, reason: REASON.INSECURE };
        return { ok: true, reason: REASON.OK };
    }

    function permission() {
        if (!navigator.geolocation) return Promise.resolve('unsupported');
        if (!navigator.permissions || !navigator.permissions.query) {
            return Promise.resolve('unknown');
        }
        return navigator.permissions.query({ name: 'geolocation' })
            .then(function (r) { return r.state; })
            .catch(function () { return 'unknown'; });
    }

    /* ── Normalised errors ──────────────────────────────────────────────── */

    function GeoError(reason, code) {
        var e = new Error('geolocation: ' + reason);
        e.name = 'GeolocationUnavailable';
        e.reason = reason;
        e.code = code == null ? null : code;
        return e;
    }

    function reasonOf(err) {
        if (!err) return REASON.UNKNOWN;
        if (err.reason) return err.reason;
        if (err.code === 1) return REASON.DENIED;
        if (err.code === 2) return REASON.UNAVAILABLE;
        if (err.code === 3) return REASON.TIMEOUT;
        return REASON.UNKNOWN;
    }

    /* ── get() ──────────────────────────────────────────────────────────── */

    // The low-accuracy second chance is scoped to THIS call. /share-location
    // and /live-location both latched it on a module-level `_retried` flag that
    // was never reset, so the user's second attempt was strictly weaker than
    // their first — the retry silently lost its own fallback.
    function get(opts) {
        opts = opts || {};
        var pre = available();
        if (!pre.ok) {
            report(pre.reason, null);
            return Promise.reject(GeoError(pre.reason, null));
        }
        var highOpts = {
            enableHighAccuracy: opts.enableHighAccuracy !== false,
            timeout: opts.timeout || 20000,
            maximumAge: opts.maximumAge == null ? 0 : opts.maximumAge
        };
        var allowFallback = opts.lowAccuracyFallback !== false;

        return new Promise(function (resolve, reject) {
            var settled = false;
            function ok(pos) { if (!settled) { settled = true; resolve(pos); } }
            function fail(err) {
                if (settled) return;
                settled = true;
                var r = reasonOf(err);
                report(r, err && err.code);
                reject(GeoError(r, err && err.code));
            }
            navigator.geolocation.getCurrentPosition(ok, function (err) {
                // Only a TIMEOUT is worth a cheaper second attempt: a denial
                // re-fails in under 35 ms (measured) and an unavailable fix is
                // not made available by asking less precisely.
                if (err && err.code === 3 && allowFallback) {
                    navigator.geolocation.getCurrentPosition(ok, fail, {
                        enableHighAccuracy: false,
                        timeout: opts.fallbackTimeout || 30000,
                        maximumAge: 60000
                    });
                    return;
                }
                fail(err);
            }, highOpts);
        });
    }

    function watch(onPos, onErr, opts) {
        var pre = available();
        if (!pre.ok) {
            report(pre.reason, null);
            if (onErr) onErr(GeoError(pre.reason, null));
            return null;
        }
        opts = opts || {};
        return navigator.geolocation.watchPosition(onPos, function (err) {
            var r = reasonOf(err);
            report(r, err && err.code);
            if (onErr) onErr(GeoError(r, err && err.code));
        }, {
            enableHighAccuracy: opts.enableHighAccuracy !== false,
            maximumAge: opts.maximumAge == null ? 3000 : opts.maximumAge,
            timeout: opts.timeout || 30000
        });
    }

    function clear(id) {
        if (id == null || !navigator.geolocation) return;
        try { navigator.geolocation.clearWatch(id); } catch (e) {}
    }

    /* ── explain() ──────────────────────────────────────────────────────── */

    function steps(list) {
        return (list || '').split('|').map(function (s) { return s.trim(); }).filter(Boolean);
    }

    function explain(errOrReason) {
        var reason = typeof errOrReason === 'string' ? errOrReason : reasonOf(errOrReason);
        var code = (errOrReason && typeof errOrReason === 'object') ? errOrReason.code : null;
        var p = platform();
        var out = { reason: reason, code: code == null ? null : code, canRetry: false, steps: [] };

        if (reason === REASON.NO_API) {
            // NOT "your browser is too old". The only realistic cause is a
            // flipped privacy pref — Gecko's geo.enabled, shipped false by
            // hardened Android builds. Name the setting, do not blame the user.
            out.title = t('off_title', 'Location is switched off in this browser');
            out.message = t('off_text', 'This browser has its location feature disabled, so the page cannot ask for your position.');
            out.steps = steps(isGecko()
                ? t('off_step_firefox', 'Open about:config and accept the warning|Search for geo.enabled|Set it to true|Reload this page')
                : t('off_step_generic', 'Open your browser settings and turn the location feature back on|Reload this page'));
        } else if (reason === REASON.INSECURE) {
            out.title = t('insecure_title', 'Location needs a secure page');
            out.message = t('insecure_text', 'Browsers only share your position over https. Open this page at its https address.');
        } else if (reason === REASON.DENIED) {
            // canRetry stays false: a repeat call re-fails in <35 ms without a
            // prompt (measured in both engines), so a button could only redraw
            // this same panel. onRecover() handles the real recovery instead.
            out.title = t('denied_title', 'Location access was declined');
            out.message = t('denied_text', 'This page is blocked from using your location. Here is how to allow it again:');
            out.steps = steps(p === 'ios' ? t('step_ios', '')
                : p === 'android' ? t('step_android', '')
                : t('step_desktop', ''));
            if (!out.steps.length) {
                out.steps = steps('Open the site permissions for this page|Set Location to "Allow"|Reload the page');
            }
        } else if (reason === REASON.UNAVAILABLE) {
            out.title = t('unavailable_title', 'Your position is not available yet');
            out.message = t('unavailable_text', 'The device could not work out where it is. Check that location is switched on in the system settings, then try again.');
            out.canRetry = true;
        } else if (reason === REASON.TIMEOUT) {
            out.title = t('timeout_title', 'Locating took too long');
            out.message = t('timeout_text', 'The first fix can take a minute outdoors and may never arrive deep inside a building. Move towards a window and try again.');
            out.canRetry = true;
        } else {
            out.title = t('generic_title', 'Could not get your location');
            out.message = t('generic_text', 'Something stopped the browser from reporting your position. Try again in a moment.');
            out.canRetry = true;
        }
        return out;
    }

    /* ── notify() ───────────────────────────────────────────────────────── */

    // For tools where locating is a convenience (a "locate me" button on a map
    // that works fine without it) a full panel is too heavy — but silence is
    // worse. These were the 8 call sites doing a bare `return`: the user
    // pressed the button and the page did nothing whatsoever, which is the same
    // dead-click the /share-location bug report was about.
    //
    // ⚠️ Шаги — часть ответа, а не украшение панели. Фраза отказа кончается
    // словами «Вот как вернуть доступ:», и до 07.10.2026 тост обрывался на
    // двоеточии: шаги рисовала только panel(), а notify() их выбрасывал (так
    // отвечали «Рядом со мной» карты гроз, землетрясений, сияний и глобус).
    // С шагами тост живёт, пока человек его не закроет: за четыре секунды
    // инструкцию из трёх действий не прочесть, тем более не выполнить.
    function notifyText(exp) {
        var text = exp.title + ' ' + exp.message;
        if (exp.steps && exp.steps.length) {
            text += ' ' + exp.steps.map(function (s, i) { return (i + 1) + ') ' + s; }).join('; ') + '.';
        }
        return text;
    }

    function notify(errOrReason) {
        var exp = explain(errOrReason);
        try {
            if (window.__toast) window.__toast(notifyText(exp), 'warning', exp.steps && exp.steps.length ? { keep: true } : undefined);
            else if (window.__announce) window.__announce(exp.title);
        } catch (e) {}
        return exp;
    }

    /* ── onRecover() ────────────────────────────────────────────────────── */

    // The honest answer to a denial. The user has to leave for the browser or
    // OS settings; when they come back we re-probe and heal, so they never face
    // a button that provably cannot work.
    function onRecover(cb) {
        if (typeof cb !== 'function' || !navigator.geolocation) return function () {};
        var stopped = false, fired = false;
        function fire() {
            if (stopped || fired) return;
            fired = true;
            try { cb(); } catch (e) {}
        }
        var statusRef = null;
        function onChange() { if (statusRef && statusRef.state === 'granted') fire(); }
        if (navigator.permissions && navigator.permissions.query) {
            navigator.permissions.query({ name: 'geolocation' }).then(function (s) {
                statusRef = s;
                s.addEventListener('change', onChange);
            }).catch(function () {});
        }
        // Firefox for Android has historically not fired the change event for
        // geolocation, so a returning tab is re-probed directly.
        function onVis() {
            if (document.visibilityState !== 'visible' || stopped || fired) return;
            permission().then(function (state) { if (state === 'granted') fire(); });
        }
        document.addEventListener('visibilitychange', onVis);
        return function () {
            stopped = true;
            document.removeEventListener('visibilitychange', onVis);
            if (statusRef) { try { statusRef.removeEventListener('change', onChange); } catch (e) {} }
        };
    }

    /* ── panel() ────────────────────────────────────────────────────────── */

    // Default renderer. Tools with their own tuned panel (gps-speedometer) use
    // explain() and keep their markup; everything else gets a consistent one
    // for free, so "unavailable" never renders as a blank screen again.
    function panel(host, exp, opts) {
        if (!host) return;
        opts = opts || {};
        host.textContent = '';
        host.classList.add('geo-panel');
        host.setAttribute('role', 'status');

        var icon = document.createElement('div');
        icon.className = 'geo-panel-icon';
        icon.setAttribute('aria-hidden', 'true');
        icon.textContent = '📍';
        host.appendChild(icon);

        var h = document.createElement('h3');
        h.className = 'geo-panel-title';
        h.textContent = exp.title;
        host.appendChild(h);

        var p = document.createElement('p');
        p.className = 'geo-panel-text';
        p.textContent = exp.message;
        host.appendChild(p);

        if (exp.steps && exp.steps.length) {
            var ol = document.createElement('ol');
            ol.className = 'geo-panel-steps';
            exp.steps.forEach(function (s) {
                var li = document.createElement('li');
                li.textContent = s;
                ol.appendChild(li);
            });
            host.appendChild(ol);
        }

        var actions = document.createElement('div');
        actions.className = 'geo-panel-actions';
        // The retry button appears ONLY where retrying can actually succeed.
        if (exp.canRetry && opts.onRetry) {
            var b = document.createElement('button');
            b.type = 'button';
            b.className = 'geo-panel-btn';
            b.textContent = t('retry', 'Try again');
            b.addEventListener('click', function () { opts.onRetry(); });
            actions.appendChild(b);
        }
        if (opts.altLabel && opts.onAlt) {
            var a = document.createElement('button');
            a.type = 'button';
            // Ghost only when it sits BESIDE a retry. When retrying is
            // impossible (a denial, a missing API) this is the single way
            // forward, and a secondary-looking button reads as "optional".
            a.className = 'geo-panel-btn' + (actions.childNodes.length ? ' ghost' : '');
            a.textContent = opts.altLabel;
            a.addEventListener('click', function () { opts.onAlt(); });
            actions.appendChild(a);
        }
        if (actions.childNodes.length) host.appendChild(actions);
        host.classList.remove('hidden');
    }

    window.__geo = {
        REASON: REASON,
        available: available,
        permission: permission,
        get: get,
        watch: watch,
        clear: clear,
        explain: explain,
        notify: notify,
        panel: panel,
        onRecover: onRecover,
        _report: report
    };
})();
